// autosaveStatus.tsx
// Drop-in hook + UI badge for "Saving…/Saved" status on every change
// Works with any form state (react-hook-form, useState, Zustand, etc.)

import { CircleNotch, CloudCheck, Recycle } from "@phosphor-icons/react";
import React from "react";

export type SaveStatus = "idle" | "editing" | "saving" | "saved" | "error" | "offline";

export interface UseAutosaveOptions<T> {
  // Called when we need to persist. Must throw on failure.
  // If you support aborting, honor the AbortSignal.
  saveFn: (payload: T, signal?: AbortSignal) => Promise<void>;
  // Values to watch. Whenever this object changes, we schedule a save.
  payload?: T;
  // Debounce before we start saving (ms)
  debounceMs?: number; // default 600
  // Minimum time to show the "saving" state so it doesn't flicker (ms)
  minSavingMs?: number; // default 300
  // How long to keep the "saved" pill visible after success (ms)
  savedVisibleMs?: number; // default 1200
  // Optional: pause autosave (e.g., during bulk import)
  paused?: boolean;
}

export function useAutosaveStatus<T>({
  saveFn,
  payload,
  debounceMs = 600,
  minSavingMs = 300,
  savedVisibleMs = 1200,
  paused = false,
}: UseAutosaveOptions<T>) {
  const [status, setStatus] = React.useState<SaveStatus>("idle");
  const [error, setError] = React.useState<string | null>(null);
  const [lastSavedAt, setLastSavedAt] = React.useState<number | null>(null);
  const [isDirty, setIsDirty] = React.useState(false);

  const debounceTimer = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const savingStartRef = React.useRef<number>(0);
  const opIdRef = React.useRef(0); // guards against races
  const abortRef = React.useRef<AbortController | null>(null);

  // Cancel any scheduled or in-flight save
  const cancel = React.useCallback(() => {
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
      debounceTimer.current = null;
    }
    if (abortRef.current) {
      abortRef.current.abort();
      abortRef.current = null;
    }
  }, []);

  // Manually trigger save (e.g., Cmd+S)
  const triggerSave = React.useCallback(
    async (manualPayload?: T) => {
      cancel();
      setStatus((s) => (s === "saving" ? s : "saving"));
      savingStartRef.current = Date.now();
      setError(null);

      const myOpId = ++opIdRef.current;
      const ac = new AbortController();
      abortRef.current = ac;

      try {
        await saveFn(manualPayload ?? (payload as T), ac.signal);
        // ensure min saving visibility
        const elapsed = Date.now() - savingStartRef.current;
        if (elapsed < minSavingMs) {
          await new Promise((r) => setTimeout(r, minSavingMs - elapsed));
        }
        if (myOpId === opIdRef.current) {
          setStatus("saved");
          setIsDirty(false);
          setLastSavedAt(Date.now());
          // fade out saved
          const t = setTimeout(() => {
            setStatus("idle");
          }, savedVisibleMs);
          return () => clearTimeout(t);
        }
      } catch (e: any) {
        // Distinguish offline vs error if possible
        const offline = typeof navigator !== "undefined" && navigator.onLine === false;
        if (myOpId === opIdRef.current) {
          setStatus(offline ? "offline" : "error");
          setError(e?.message ?? String(e));
        }
      } finally {
        if (abortRef.current === ac) abortRef.current = null;
      }
    },
    [cancel, minSavingMs, payload, saveFn, savedVisibleMs],
  );

  // Schedule autosave when payload changes
  React.useEffect(() => {
    if (paused) return;
    if (payload === undefined) return;
    setIsDirty(true);
    setStatus((s) => (s === "saving" ? s : "editing"));
    setError(null);

    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(() => {
      triggerSave();
    }, debounceMs);

    return () => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
        debounceTimer.current = null;
      }
    };
  }, [payload, debounceMs, paused, triggerSave]);

  // Cleanup on unmount
  React.useEffect(() => cancel, [cancel]);

  return { status, error, lastSavedAt, isDirty, triggerSave, cancel } as const;
}

// Resume Saving/Saved status indicator
export function SaveStatusBadge({
  status,
  compact = false,
  className = "",
}: {
  status: SaveStatus;
  compact?: boolean;
  className?: string;
}) {
  const text =
    status === "saving"
      ? "Saving…"
      : status === "saved"
        ? "Saved"
        : status === "editing"
          ? "Editing…"
          : status === "offline"
            ? "Offline — will retry"
            : status === "error"
              ? "Failed to save"
              : status === "idle"
                ? "Up-to date"
                : "";

  // Tailwind styles; adjust to your design system.
  const base =
    "inline-flex items-center gap-1.5 rounded-md px-2.5 py-0.5 text-xs w-max font-normal sm:border";
  const tone =
    status === "saving"
      ? "bg-amber-50 border-amber-100 text-amber-700"
      : status === "saved"
        ? "bg-emerald-50 border-emerald-100 text-emerald-700"
        : status === "editing"
          ? "bg-sky-50 border-sky-100 text-sky-700"
          : status === "offline"
            ? "bg-gray-100 border-gray-200 text-gray-600"
            : "bg-rose-50 border-rose-100 text-rose-700"; // error

  if (text.length === 0) {
    return (
      <span className={[base, compact ? "px-2 py-0.5 text-xs" : "", className].join(" ")}>
        <CloudCheck size={16} />
      </span>
    );
  }

  return (
    <span className={[base, compact ? "px-2 py-0.5 text-xs" : "", className].join(" ")}>
      {status === "saving" && <CircleNotch size={16} className="animate-spin" />}
      {(status === "saved" || status === "idle") && <CloudCheck size={16} />}
      <span className="hidden sm:inline">{text}</span>
    </span>
  );
}
